// utils/format.ts
import { dynamicImage } from "../../../Service";
import { PersonalInformation } from "../../../Types/UserProfileDetails";
import { EmployeeFieldKey } from "./types";
import {
  male,
  female,
  userImg,
  maleUserImg,
  femaleUserImg,
} from "../../../utils/Constant";

export const areAllValuesEmpty = (
  obj?: Record<string, any> | null,
  excludeKeys: string[] = []
): boolean => {
  if (!obj || typeof obj !== "object") return true;

  return Object.entries(obj).every(([key, value]) => {
    if (excludeKeys.includes(key)) return true;

    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "");

    if (typeof value === "object" && !Array.isArray(value) && value !== null) {
      return areAllValuesEmpty(value, excludeKeys);
    }

    return isEmpty;
  });
};

export const formatAddress = (
  data: PersonalInformation,
  prefix: "current" | "permanent"
) => {
  const street = (data as any)[`${prefix}Street`];
  const address = (data as any)[`${prefix}Address`];
  const city = (data as any)[`${prefix}City`];
  const state = (data as any)[`${prefix}State`];
  const pincode = (data as any)[`${prefix}Pincode`];

  const line1 = [street, address].filter(Boolean).join(", ");
  const line2 = [city, state].filter(Boolean).join(", ");
  const line3 = [pincode].filter(Boolean).join(", ");

  return [line1, line2, line3].filter(Boolean).join(", ");
};

export const employeeGetOrderedEntries = <T extends object>(
  obj: T,
  omitKeys?: (keyof T | string)[],
  fieldOrder?: (keyof T | string)[]
): [EmployeeFieldKey, unknown][] => {
  const entries = Object.entries(obj ?? {}) as [EmployeeFieldKey, unknown][];
  const omitted = new Set((omitKeys ?? []).map(String));
  const visible = entries.filter(([k]) => !omitted.has(k));

  if (!fieldOrder?.length) return visible;

  const orderKeys = fieldOrder.map(String);
  const orderSet = new Set(orderKeys);

  const inOrder = orderKeys
    .map((k) => visible.find(([key]) => key === k))
    .filter((e): e is [EmployeeFieldKey, unknown] => Boolean(e));

  const remaining = visible.filter(([k]) => !orderSet.has(k));
  return [...inOrder, ...remaining];
};

export const getProfileImage = (profile: string | null, gender: string) => {
  if (profile) return profile;
  if (gender === male) return dynamicImage(maleUserImg);
  if (gender === female) return dynamicImage(femaleUserImg);
  return dynamicImage(userImg);
};
